import { ArrowDownIcon, CloseIcon } from '@synergy/theme-next';
import { Autocomplete, Box, InputAdornment, Stack, styled } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Property } from 'csstype';
import { ReactElement, ReactNode } from 'react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { PoolLineClampTypography } from '../pool-line-clamp-typography/pool-line-clamp-typography';
import { PoolTextfield, PoolTextfieldProps } from '../pool-textfield';

const RenderOption = styled('li')(({ theme }) => ({
    ...theme.typography.body1,

    '&.MuiAutocomplete-option': {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 2),
    },
}));

export interface AutocompleteOption<T> {
    value: T | '';
    displayValue: string;
    endAdornment?: ReactNode;
    startAdornment?: ReactNode;
}

export interface PoolAutocompleteProps<T = number> extends Omit<PoolTextfieldProps, 'onChange'> {
    value?: T | null;
    displayValue?: string | null;
    options: AutocompleteOption<T>[];
    dropdownWidth?: Property.Width;
    onChange?: (option: T | null) => void;
    /** What the closed field shows of the chosen option, where it is narrower than the list. */
    selectedLabel?: (option: AutocompleteOption<T>) => string;
}

export const PoolAutocomplete = <T,>(props: PoolAutocompleteProps<T>): ReactElement => {
    const { value, displayValue, options, onChange, disabled, dropdownWidth, selectedLabel, ...textFieldProps } = props;
    const intl = useIntl();

    let selectedValue = options.find(option => option.value === value) ?? null;

    if (value && selectedValue === null && displayValue) {
        selectedValue = {
            value: '',
            displayValue: displayValue,
        };

        options.push(selectedValue);
    }

    const handleChange = (option: AutocompleteOption<T> | null): void => {
        if (option === null) {
            onChange?.(null);
            return;
        }
        onChange?.(option.value as T);
    };

    return (
        <Autocomplete
            autoComplete={true}
            onChange={(_, option) => handleChange(option)}
            options={options}
            filterOptions={(options, state) => {
                return options.filter(option =>
                    option.displayValue.toLowerCase().includes(state.inputValue.toLowerCase())
                );
            }}
            getOptionLabel={option => selectedLabel?.(option) ?? option.displayValue}
            renderOption={(props, option, state) => {
                const matches = match(option.displayValue, state.inputValue, { insideWords: true });
                const parts = parse(option.displayValue, matches);

                return (
                    <RenderOption {...props}>
                        {option.startAdornment}
                        <Stack direction="row">
                            <PoolLineClampTypography component="span">
                                {parts.map((part, index) => (
                                    <Box
                                        key={index}
                                        component="span"
                                        sx={part.highlight ? { color: theme => theme.palette.primary.main } : {}}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                            </PoolLineClampTypography>
                        </Stack>
                        {option.endAdornment}
                    </RenderOption>
                );
            }}
            renderInput={params => (
                <PoolTextfield
                    placeholder={intl.formatMessage({ id: 'components.autocomplete.placeholder' })}
                    {...textFieldProps}
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {textFieldProps.endAdornment && (
                                    <InputAdornment
                                        position="end"
                                        sx={{ mr: theme => theme.spacing(2), ml: 0, color: 'inherit' }}
                                    >
                                        {textFieldProps.endAdornment}
                                    </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            popupIcon={<ArrowDownIcon />}
            clearIcon={<CloseIcon />}
            value={selectedValue}
            disabled={disabled}
            sx={{
                ...(textFieldProps.endAdornment && {
                    '& .MuiAutocomplete-clearIndicator': {
                        visibility: 'visible',
                    },
                }),
            }}
            noOptionsText={<FormattedMessage id="app.options.noOptions" />}
            slotProps={{ popper: dropdownWidth && { style: { width: dropdownWidth }, placement: 'bottom-start' } }}
        />
    );
};
